/**
 * Serving Firebase's auth handler from the host somebody is actually browsing.
 *
 * THE PROBLEM IT SOLVES. `signInWithRedirect` stores its pending state on the `authDomain`
 * origin and reads it back after the round trip. With the app on `nmm.aalrr.com` and the auth
 * domain on `aalrrnewclientintake.firebaseapp.com`, that read is a cross-site storage access,
 * which Edge blocks — so people pick their account at Microsoft and arrive back signed out. The
 * button was never affected, because a popup returns the session through `window.opener`.
 *
 * Firebase Hosting serves `/__/auth/handler` on every domain attached to the project, so pointing
 * `authDomain` at the current host makes the whole flow same-origin and the block irrelevant.
 *
 * WHY EACH HOST IS SWITCHED ON SEPARATELY. A wrong `authDomain` breaks EVERY sign-in, the button
 * included — there is no degraded mode, just a locked door for the whole firm. And the SAML
 * request names the host as its Assertion Consumer Service URL, which Entra refuses unless that
 * exact URL is a registered reply URL. So a host goes onto HOSTS_DEFAULT_ON only once a real
 * sign-in has been completed through it; until then it is opt-in per browser:
 *
 *     https://conflicts.aalrr.com/?sameorigin=1     on, for this browser
 *     https://conflicts.aalrr.com/?sameorigin=0     off, for this browser — also the way out
 *                                                   on a host where it is on by default
 *
 * ONLY FOR HOSTS THAT SERVE THE HANDLER. A host outside HOSTS_WITH_HANDLER has no
 * `/__/auth/handler`, and pointing the SDK at one would produce exactly the outage this is trying
 * to avoid. localhost is excluded too: the emulator has its own arrangement.
 */

const FLAG = 'nmi.auth.sameOrigin';
const PARAM = 'sameorigin';

/** Domains attached to this Firebase Hosting project, which therefore serve /__/auth/handler. */
const HOSTS_WITH_HANDLER = [
  'nmm.aalrr.com',
  'conflicts.aalrr.com',
  'candidateconflicts.aalrr.com',
  'aalrrnewclientintake.web.app',
  'aalrrnewclientintake.firebaseapp.com',
];

/**
 * Hosts where same-origin is the default, because a sign-in has been completed through them.
 * nmm.aalrr.com was proved on 2026-09-25; the firebaseapp.com host IS the configured auth domain,
 * so same-origin there changes nothing. The others wait for their reply URLs to be confirmed.
 */
const HOSTS_DEFAULT_ON = ['nmm.aalrr.com', 'aalrrnewclientintake.firebaseapp.com'];

/** What this browser was told last: 'yes', 'no', or nothing. */
const read = () => {
  try {
    return window.localStorage.getItem(FLAG);
  } catch {
    return null;
  }
};

const write = (value) => {
  try {
    window.localStorage.setItem(FLAG, value);
  } catch {
    /* a preference that cannot be stored simply is not remembered */
  }
};

/**
 * The auth domain to use, given where we are and what this browser has been told.
 *
 * @param {object} args
 * @param {string} args.hostname     window.location.hostname
 * @param {string} args.search       window.location.search
 * @param {string} args.fallback     the configured VITE_FIREBASE_AUTH_DOMAIN
 * @param {boolean} [args.persist]   false in tests, so reading does not write
 * @param {string|null} [args.stored] what the browser remembers; tests pass it, the app reads it
 * @returns {{ authDomain: string, sameOrigin: boolean }}
 */
export function resolveAuthDomain({ hostname, search, fallback, persist = true, stored }) {
  const host = String(hostname || '').toLowerCase();
  const off = { authDomain: fallback, sameOrigin: false };

  // An explicit instruction in the URL wins and is remembered for this browser.
  const param = new URLSearchParams(String(search || '')).get(PARAM);
  const askedOn = param === '1' || param === 'true';
  const askedOff = param === '0' || param === 'false';
  if (askedOn && persist) write('yes');
  if (askedOff) {
    if (persist) write('no');
    return off;
  }

  const remembered = stored === undefined ? (persist ? read() : null) : stored;
  if (remembered === 'no' && !askedOn) return off;

  const wanted = askedOn || remembered === 'yes' || HOSTS_DEFAULT_ON.includes(host);
  if (!wanted) return off;
  if (!HOSTS_WITH_HANDLER.includes(host)) return off;

  return { authDomain: host, sameOrigin: true };
}

export { FLAG, PARAM, HOSTS_WITH_HANDLER, HOSTS_DEFAULT_ON };
